import firebase from 'firebase'
import { groups, groupMembers, groupDomains } from '../../firebase/collections'
import { slugify, makeUniqueSlug } from '../../utils/slugify'

const PERMISSION_ERROR =
  'You do not have permission to modify this workspace'

const ROLE_PERMISSIONS = {
  owner: ['add', 'edit', 'delete', 'manage', 'invite'],
  admin: ['add', 'edit', 'delete'],
  editor: ['add', 'edit'],
  member: []
}

export function roleCan(role, action) {
  return (ROLE_PERMISSIONS[role] || []).includes(action)
}

/**
 * Assert the current user may perform a workspace action.
 * Owner: add, edit, delete domains and invite members.
 * Admin: add, edit, delete domains.
 * Editor: add and edit domains.
 *
 * @param {string} groupToken
 * @param {'add'|'edit'|'delete'|'manage'|'invite'} action
 * @returns {Promise<{ group: object, role: string }>}
 */
export async function assertWorkspaceCapability(groupToken, action = 'manage') {
  const currentUser = firebase.auth().currentUser
  if (!currentUser) {
    throw new Error('User not authenticated')
  }

  const groupDoc = await groups.doc(groupToken).get()
  if (!groupDoc.exists) {
    throw new Error('Workspace not found')
  }

  const group = groupDoc.data()

  if (group.ownerId === currentUser.uid) {
    return { group, role: 'owner' }
  }

  const memberDoc = await groupMembers
    .doc(groupToken)
    .collection('members')
    .doc(currentUser.uid)
    .get()

  const memberData = memberDoc.exists ? memberDoc.data() : null
  const memberStatus = memberData?.status || 'active'
  const role =
    memberData && memberStatus === 'active'
      ? memberData.role || 'member'
      : 'member'

  if (roleCan(role, action)) {
    return { group, role }
  }

  throw new Error(PERMISSION_ERROR)
}

// Membership management uses groups.ownerId. A member role of "owner" does not grant access.
export async function assertWorkspaceOwner(groupToken) {
  const currentUser = firebase.auth().currentUser
  if (!currentUser) {
    throw new Error('User not authenticated')
  }

  const groupDoc = await groups.doc(groupToken).get()
  if (!groupDoc.exists) {
    throw new Error('Workspace not found')
  }

  const group = groupDoc.data()
  if (group.ownerId !== currentUser.uid) {
    throw new Error('Only the workspace owner can manage members')
  }

  return { group, role: 'owner' }
}

export function isProtectedWorkspaceOwner(member, ownerId) {
  if (!member) return false
  return member.role === 'owner' || (!!ownerId && member.uid === ownerId)
}

/** @deprecated Prefer assertWorkspaceCapability(groupToken, action) */
export async function assertGroupOwner(groupToken) {
  const result = await assertWorkspaceCapability(groupToken, 'manage')
  return result.group
}

export default {
  // Create a new group and optionally add a member
  async createGroupAccess(
    groupName,
    token,
    uid = null,
    email = null,
    description = ''
  ) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      // Generate slug from group name
      const baseSlug = slugify(groupName)

      // Get existing slugs to ensure uniqueness
      const existingGroups = await groups.get()
      const existingSlugs = existingGroups.docs
        .map(doc => doc.data().slug)
        .filter(slug => slug) // Filter out any undefined slugs

      // Make slug unique if necessary
      const uniqueSlug = makeUniqueSlug(baseSlug, existingSlugs)

      // 1. Create the group document
      await groups.doc(token).set({
        id: token,
        name: groupName,
        groupName: groupName,
        description: description || '',
        slug: uniqueSlug,
        ownerId: currentUser.uid,
        settings: {
          isPrivate: true, // Default to private. When false, workspace uses public URL format
          allowMemberInvites: false,
          memberPermissions: {
            addBookmarks: false,
            editBookmarks: false,
            deleteBookmarks: false
          }
        },
        createdAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      })

      // 2. Create members collection and add owner
      const membersRef = groupMembers.doc(token).collection('members')

      // Create owner member document (active by default)
      await membersRef.doc(currentUser.uid).set({
        role: 'owner',
        status: 'active',
        joinedAt: firebase.firestore.FieldValue.serverTimestamp(),
        invitedBy: currentUser.uid
      })

      // Only create member document if uid is provided
      if (uid) {
        await membersRef.doc(uid).set({
          role: 'member',
          status: 'pending',
          joinedAt: firebase.firestore.FieldValue.serverTimestamp(),
          invitedBy: currentUser.uid
        })
      }
      // Store pending invitation by email if no uid but email is provided
      else if (email) {
        // Create a special document in the members collection to track pending invitations by email
        // We'll use a consistent prefix to identify these documents
        const inviteId = `pending_${email.replace(/[.@]/g, '_')}`

        await membersRef.doc(inviteId).set({
          email: email,
          role: 'member',
          status: 'pending_registration',
          invitedAt: firebase.firestore.FieldValue.serverTimestamp(),
          invitedBy: currentUser.uid
        })
      }

      return { success: true, slug: uniqueSlug, token: token }
    } catch (error) {
      console.error('Error creating group access:', error)
      throw error
    }
  },

  // Add new members to an existing group
  async updateGroupAccess(group) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      // Only workspace owners can invite new members.
      await assertWorkspaceCapability(group.token, 'invite')

      // Add the new member
      const memberRef = groupMembers
        .doc(group.token)
        .collection('members')
        .doc(group.uid)

      await memberRef.set({
        role: 'member',
        status: 'pending',
        joinedAt: firebase.firestore.FieldValue.serverTimestamp(),
        invitedBy: currentUser.uid
      })
    } catch (error) {
      console.error('Error updating group access:', error)
      throw error
    }
  },

  // New method to handle invites for non-registered users
  async updateGroupAccessByEmail(token, email) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      // Check if there's a pending invitation for this email
      const membersRef = groupMembers.doc(token).collection('members')
      const inviteId = `pending_${email.replace(/[.@]/g, '_')}`
      const pendingInvite = await membersRef.doc(inviteId).get()

      if (pendingInvite.exists) {
        // Create a regular member entry for the user
        await membersRef.doc(currentUser.uid).set({
          role: pendingInvite.data().role || 'member',
          status: 'active',
          joinedAt: firebase.firestore.FieldValue.serverTimestamp(),
          invitedAt: pendingInvite.data().invitedAt,
          invitedBy: pendingInvite.data().invitedBy
        })

        // Delete the pending invitation
        await membersRef.doc(inviteId).delete()
        return { success: true }
      } else {
        return {
          success: false,
          message: 'No pending invitation found for this email'
        }
      }
    } catch (error) {
      console.error('Error updating group access by email:', error)
      throw error
    }
  },

  async addGroupDomain(groupToken, domainData) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      await assertWorkspaceCapability(groupToken, 'add')

      const querySnapshot = await groupDomains
        .doc(groupToken)
        .collection('categories')
        .where('link', '==', domainData.link)
        .where('added_by', '==', currentUser.uid)
        .get()

      if (!querySnapshot.empty) {
        throw new Error(
          'This link has already been added to the selected workspace'
        )
      }

      const newDomainRef = groupDomains
        .doc(groupToken)
        .collection('categories')
        .doc()

      const now = firebase.firestore.Timestamp.fromDate(new Date())
      const domainToAdd = {
        ...domainData,
        groupToken,
        doc_id: newDomainRef.id,
        added_by: currentUser.uid,
        date_added: domainData.date_added || now,
        date_created: domainData.date_created || now,
        date_updated: now
      }

      await newDomainRef.set(domainToAdd)
      return domainToAdd
    } catch (error) {
      console.error('Error adding domain to group:', error)
      throw error
    }
  },

  async updateGroupDomain(groupToken, domainId, data) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      await assertWorkspaceCapability(groupToken, 'edit')

      const domainRef = groupDomains
        .doc(groupToken)
        .collection('categories')
        .doc(domainId)

      const updateData = {
        ...data,
        date_updated: firebase.firestore.Timestamp.now()
      }

      await domainRef.update(updateData)
      return updateData
    } catch (error) {
      console.error('Error updating domain in group:', error)
      throw error
    }
  },

  async deleteGroupDomain(groupToken, domainId) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      await assertWorkspaceCapability(groupToken, 'delete')

      await groupDomains
        .doc(groupToken)
        .collection('categories')
        .doc(domainId)
        .delete()

      return { success: true }
    } catch (error) {
      console.error('Error deleting domain from group:', error)
      throw error
    }
  },

  async moveGroupDomain(sourceToken, targetToken, domainId, data) {
    try {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) {
        throw new Error('User not authenticated')
      }

      await assertWorkspaceCapability(sourceToken, 'delete')
      await assertWorkspaceCapability(targetToken, 'add')

      const oldDomainRef = groupDomains
        .doc(sourceToken)
        .collection('categories')
        .doc(domainId)

      const newDomainRef = groupDomains
        .doc(targetToken)
        .collection('categories')
        .doc()

      const now = firebase.firestore.Timestamp.now()
      const movedDomain = {
        ...data,
        groupToken: targetToken,
        doc_id: newDomainRef.id,
        added_by: currentUser.uid,
        date_added: now,
        date_updated: now
      }

      await newDomainRef.set(movedDomain)
      await oldDomainRef.delete()

      return movedDomain
    } catch (error) {
      console.error('Error moving domain between groups:', error)
      throw error
    }
  },

  /**
   * Toggle workspace visibility between private and public
   * Private: /teams/workspace/{slug}
   * Public: /teams/workspace/{workspaceId}/{slug}
   *
   * @param {string} groupToken - The group token/ID
   * @param {boolean} makePublic - True to make public, false for private
   * @returns {Promise<Object>} Updated group data
   */
  async toggleWorkspaceVisibility(groupToken, makePublic) {
    try {
      await assertWorkspaceCapability(groupToken, 'manage')
      const groupDoc = await groups.doc(groupToken).get()
      const groupData = groupDoc.data()

      // Check if group has a slug - required for public workspaces
      if (makePublic && !groupData.slug) {
        // Auto-generate slug if missing
        const baseSlug = slugify(
          groupData.name || groupData.groupName || 'workspace'
        )

        // Get existing slugs to ensure uniqueness
        const existingGroups = await groups.get()
        const existingSlugs = existingGroups.docs
          .filter(doc => doc.id !== groupToken)
          .map(doc => doc.data().slug)
          .filter(slug => slug)

        const uniqueSlug = makeUniqueSlug(baseSlug, existingSlugs)

        // Update with both slug and visibility
        await groups.doc(groupToken).update({
          slug: uniqueSlug,
          'settings.isPrivate': !makePublic,
          updatedAt: firebase.firestore.FieldValue.serverTimestamp()
        })

        return {
          success: true,
          isPublic: makePublic,
          slugGenerated: true,
          slug: uniqueSlug,
          message: 'Workspace is now public (URL slug generated automatically)'
        }
      }

      // Update visibility setting
      await groups.doc(groupToken).update({
        'settings.isPrivate': !makePublic,
        updatedAt: firebase.firestore.FieldValue.serverTimestamp()
      })

      return {
        success: true,
        isPublic: makePublic,
        message: makePublic
          ? 'Workspace is now public'
          : 'Workspace is now private'
      }
    } catch (error) {
      console.error('Error toggling workspace visibility:', error)
      throw error
    }
  },

  /**
   * Get workspace visibility status
   *
   * @param {string} groupToken - The group token/ID
   * @returns {Promise<boolean>} True if public, false if private
   */
  async getWorkspaceVisibility(groupToken) {
    try {
      const groupDoc = await groups.doc(groupToken).get()
      if (!groupDoc.exists) {
        throw new Error('Group not found')
      }

      const groupData = groupDoc.data()
      return groupData.settings?.isPrivate === false
    } catch (error) {
      console.error('Error getting workspace visibility:', error)
      throw error
    }
  }
}
